/**
 * @suite/topbar — the ONE React adapter for the suite's served top bar.
 *
 * chrome.js (served live by the hub at :4040) owns the `<suite-topbar>`
 * custom element: layout, right cluster, hide/show and chat entry — edited
 * once for every application. This adapter owns the app-side hand-over, so
 * the ~18 hand-copied per-app adapters collapse into per-app CONFIG:
 *
 *   <SuiteTopbarAdapter
 *       app="hat"
 *       nav={nav} fileMenu={fileMenu} languages={languages}
 *       events={{ search, settings, language }}
 *   />
 *
 * Feature surface (previously drifted across per-app forks, now features):
 * - props-driven config (hat/hey/oda style) — the primary contract
 * - store-driven hints stay live: `nav`/`fileMenu` accept functions,
 *   re-read on every render
 * - `tokenShim`: CSS text mapping app-local token names onto the suite
 *   vocabulary, injected once (kor/yol/sat/bak style apps)
 * - `publishHeight`: ResizeObserver writing the rendered bar height into a
 *   CSS variable for layouts that must reserve exact space (duy style)
 * - `fallback`: rendered when the served chrome.js never loaded (standalone
 *   dev without the hub) so the app still shows a functional strip (kod style)
 * - `customSections`: app-aware sections rendered next to the bar for apps
 *   chrome.js cannot model generically (kat/yap escape hatch)
 * - `children`: rendered INSIDE the bar's slot area (yaz style action slot)
 */

import { Fragment, createElement, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface TopbarNavItem {
    key: string;
    /** Icon-only buttons may omit it (the title then carries the name) */
    label?: string;
    title?: string;
    icon?: string;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    /** Preload hook chrome.js may fire on hover (lazy overlay warm-up) */
    onIntent?: () => void;
    hidden?: boolean;
    /** App-specific item payloads pass through the boundary untouched */
    [key: string]: unknown;
}

export interface TopbarFileMenuItem {
    key?: string;
    type?: "theme";
    label?: string;
    icon?: string;
    hint?: string;
    danger?: boolean;
    onClick?: () => void;
    /** Preload hook chrome.js may fire on hover (lazy overlay warm-up) */
    onIntent?: () => void;
    /** App-specific item payloads pass through the boundary untouched */
    [key: string]: unknown;
}

export interface TopbarFileMenuSection {
    label?: string;
    items: TopbarFileMenuItem[];
}

/** A row of a right-cluster host menu (chrome.js `menus` contract). */
export interface TopbarHostMenuItem {
    key?: string;
    type?: "separator";
    label?: string;
    icon?: string;
    hint?: string;
    active?: boolean;
    danger?: boolean;
    onClick?: () => void;
    [key: string]: unknown;
}

/** An icon button with a dropdown, rendered right of the suite bar's language
 *  button. `onOpen` runs each time the menu opens — rows are read fresh. */
export interface TopbarHostMenu {
    key: string;
    title?: string;
    icon?: string;
    onOpen?: () => void;
    items: TopbarHostMenuItem[];
}

/** A bell-dropdown row: the newest alert rows from Hey's shared pool. */
export interface TopbarNotifItem {
    key: string;
    title: string;
    body?: string;
    time?: string;
    unread?: boolean;
    onClick?: () => void;
    onIntent?: () => void;
    [key: string]: unknown;
}

/** Segmented view-mode control right of the File button (start surfaces). */
export interface TopbarViewSwitch {
    active: string;
    options: Array<{ key: string; title: string; icon?: string }>;
}

export interface TopbarIdentity {
    name: string;
    /** Optional: identity rows render the name alone when absent */
    sublabel?: string;
    avatarUrl?: string;
}

/** The chrome:* detail payloads the served bar dispatches.
 *
 *  The second `event` argument is the raw CustomEvent. chrome.js dispatches
 *  cancelable intents (`chrome:prompt`, `chrome:accent`, `chrome:theme-editor`)
 *  and falls through to its own default when nobody claims them — a handler
 *  that owns such an intent claims it with `event.preventDefault()`. */
export interface TopbarEventHandlers {
    /** Unclaimed-prompt search fallback (the bar's own search button was
     *  removed; chrome:search now only rides the prompt degrade path) */
    search?: (detail: unknown, event?: Event) => void;
    settings?: (detail: unknown, event?: Event) => void;
    language?: (detail: { code: string }, event?: Event) => void;
    theme?: (detail: { mode: string }, event?: Event) => void;
    chat?: (detail: unknown, event?: Event) => void;
    notif?: (detail: unknown, event?: Event) => void;
    bell?: (detail: unknown, event?: Event) => void;
    files?: (detail: unknown, event?: Event) => void;
    help?: (detail: unknown, event?: Event) => void;
    /** The right cluster's lead Help Center button. A host with its own Help
     *  Center panel claims it; unclaimed, chrome.js opens the hub's
     *  /help-center page in its own window (pane hand-off, Tauri window, or
     *  popup) — never an iframe inside the host's frame tree. */
    helpCenter?: (detail: unknown, event?: Event) => void;
    logout?: (detail: unknown, event?: Event) => void;
    user?: (detail: unknown, event?: Event) => void;
    /** Prompt submit (typed or voice-transcribed). Cancelable: a host with
     *  its own agent surface claims it with preventDefault(); an unclaimed
     *  prompt degrades in chrome.js to the dock, then to chrome:search. */
    prompt?: (detail: { text?: string }, event?: Event) => void;
    view?: (detail: { key?: string }, event?: Event) => void;
    nav?: (detail: { key: string }, event?: Event) => void;
    menu?: (detail: { key: string }, event?: Event) => void;
    action?: (detail: { key: string }, event?: Event) => void;
    accent?: (detail: { accent: string | null }, event?: Event) => void;
    /** The served bar's "Theme Editor…" row; a host that claims it opens the
     *  shared editor itself, otherwise chrome.js shows its iframe overlay */
    "theme-editor"?: (detail: unknown, event?: Event) => void;
    /** Generic catch-all for events an app needs before chrome.js models them */
    "host-menu"?: (detail: { menu: string; item: string }, event?: Event) => void;
}

export interface SuiteTopbarElement extends HTMLElement {
    nav: TopbarNavItem[] | null;
    /** Segmented view-mode control right of the File button */
    viewSwitch: TopbarViewSwitch | null;
    /** Application-switcher dropdown (handed in as null to hide the button) */
    appSwitchMenu: TopbarFileMenuSection[] | null;
    /** Bell dropdown rows (the bar's own "Open in Hey" footer stays) */
    notifItems: TopbarNotifItem[] | null;
    /** Optional right-cluster action buttons (undo/redo/print style) */
    actions: TopbarNavItem[] | null;
    /** Optional right-cluster dropdown menus (workspace pickers, theme rows) */
    menus: TopbarHostMenu[] | null;
    fileMenu: TopbarFileMenuSection[] | null;
    identity: TopbarIdentity | null;
    /** false omits the account menu's "Profile & Account" row — a host with
     *  no profile surface; the identity block stays (chrome.js contract) */
    profileEnabled?: boolean;
    theme: string;
    themeModes: string[];
    languages: Array<{ code: string; label: string }>;
    language: string;
    /** Localized chrome furniture (titles, aria-labels, menu rows). The host
     *  re-hands it on every render, so a language switch relabels the bar
     *  live; every chrome.js key falls back to the suite English. */
    labels?: Record<string, string> | null;
    badge: number;
    notifications?: boolean;
    chatActive?: boolean;
    promptPlaceholder?: string;
    settingsEnabled?: boolean;
    helpEnabled?: boolean;
}

export interface SuiteTopbarAdapterConfig {
    /** The register app id — chrome.js keys per-app hide state with it */
    app: string;
    nav?: TopbarNavItem[] | (() => TopbarNavItem[]);
    /** Segmented view-mode control right of the File button */
    viewSwitch?: TopbarViewSwitch | null;
    /** Application-switcher dropdown; null (default) hides the button */
    appSwitchMenu?: TopbarFileMenuSection[] | null;
    /** Bell dropdown rows; the bar's own footer still fires chrome:bell */
    notifItems?: TopbarNotifItem[] | (() => TopbarNotifItem[]);
    /** Right-cluster action buttons, re-read every render */
    actions?: TopbarNavItem[] | (() => TopbarNavItem[]);
    /** Right-cluster dropdown menus, re-read every render */
    menus?: TopbarHostMenu[] | (() => TopbarHostMenu[]);
    fileMenu?: TopbarFileMenuSection[] | (() => TopbarFileMenuSection[]);
    identity?: TopbarIdentity | null;
    /** false omits the account menu's "Profile & Account" row */
    profileEnabled?: boolean;
    theme?: string;
    themeModes?: string[];
    languages?: Array<{ code: string; label: string }>;
    language?: string;
    /** Localized chrome furniture — see SuiteTopbarElement.labels */
    labels?: Record<string, string> | null;
    badge?: number;
    notifications?: boolean;
    chatActive?: boolean;
    promptPlaceholder?: string;
    settingsEnabled?: boolean;
    helpEnabled?: boolean;
    events?: TopbarEventHandlers;
    /** CSS text mapping app token names onto the suite vocabulary */
    tokenShim?: string;
    /** ResizeObserver publish of the rendered bar height into this CSS
        variable (defaults to `--suite-topbar-height`) */
    publishHeight?: boolean | string;
    /** Rendered when the custom element is unavailable (chrome.js not loaded) */
    fallback?: ReactNode;
    /** App-aware section rendered beside the bar (kat/yap escape hatch) */
    customSections?: ReactNode;
    /** Content slotted inside the bar (yaz style action slot) */
    children?: ReactNode;
    /** Mount `children` through a portal into a slot INSIDE the bar's middle
     *  section (ahead of the AI prompt center) instead of beside the bar. If
     *  chrome.js never upgrades, the slot falls back to a bare strip appended
     *  to the unstyled element so the hosted toolbar is never lost. */
    slotChildren?: boolean;
}

/** Grace period before a never-upgrading bar (hub down) swaps to the fallback */
const CHROME_GRACE_MS = 3000;

const CHROME_EVENT_NAMES = [
    "chrome:search",
    "chrome:settings",
    "chrome:language",
    "chrome:theme",
    "chrome:chat",
    "chrome:notif",
    "chrome:bell",
    "chrome:files",
    "chrome:help",
    "chrome:help-center",
    "chrome:logout",
    "chrome:user",
    "chrome:prompt",
    "chrome:view",
    "chrome:nav",
    "chrome:menu",
    "chrome:action",
    "chrome:accent",
    "chrome:theme-editor",
    "chrome:host-menu",
] as const;

const eventNameToHandlerKey = (name: string): keyof TopbarEventHandlers =>
    name.replace("chrome:", "") as keyof TopbarEventHandlers;

let shimSeq = 0;

export function SuiteTopbarAdapter(config: SuiteTopbarAdapterConfig) {
    const {
        app,
        nav,
        actions,
        menus,
        fileMenu,
        viewSwitch = null,
        appSwitchMenu = null,
        notifItems,
        identity = null,
        profileEnabled,
        theme = "",
        themeModes = [],
        languages = [],
        language = "en",
        labels = null,
        badge = 0,
        notifications = false,
        chatActive = false,
        promptPlaceholder,
        settingsEnabled,
        helpEnabled,
        events = {},
        tokenShim,
        publishHeight = false,
        fallback,
        customSections,
        children,
        slotChildren = false,
    } = config;

    const hostRef = useRef<SuiteTopbarElement | null>(null);
    const slotRef = useRef<HTMLDivElement | null>(null);
    const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

    // Property hand-over runs after EVERY render so function-valued nav /
    // fileMenu re-read the app's stores (live shortcut hints, badges, …).
    useLayoutEffect(() => {
        const el = hostRef.current;
        if (!el) return;
        el.nav = (typeof nav === "function" ? nav() : nav) ?? null;
        el.viewSwitch = viewSwitch ?? null;
        el.appSwitchMenu = appSwitchMenu ?? null;
        el.notifItems =
            (typeof notifItems === "function" ? notifItems() : notifItems) ??
            null;
        el.actions =
            (typeof actions === "function" ? actions() : actions) ?? null;
        el.menus = (typeof menus === "function" ? menus() : menus) ?? null;
        el.fileMenu =
            (typeof fileMenu === "function" ? fileMenu() : fileMenu) ?? null;
        el.identity = identity ?? null;
        if (profileEnabled !== undefined) el.profileEnabled = profileEnabled;
        el.theme = theme;
        el.themeModes = themeModes;
        el.languages = languages;
        el.language = language;
        el.labels = labels;
        el.badge = badge;
        el.notifications = notifications;
        el.chatActive = chatActive;
        if (promptPlaceholder !== undefined) el.promptPlaceholder = promptPlaceholder;
        if (settingsEnabled !== undefined) el.settingsEnabled = settingsEnabled;
        if (helpEnabled !== undefined) el.helpEnabled = helpEnabled;
    });

    // chrome:* event bridge
    useEffect(() => {
        const el = hostRef.current;
        if (!el) return;
        const dispatchers = new Map<string, (e: Event) => void>();
        for (const name of CHROME_EVENT_NAMES) {
            const handler = events[eventNameToHandlerKey(name)];
            if (!handler) continue;
            const dispatcher = (e: Event) => {
                // Registering a handler claims a cancelable intent: chrome.js
                // falls through to its own fallback overlay only when nobody
                // preventDefault()s, so an unclaimed dispatch would raise that
                // fallback (the own-window /help-center page for
                // chrome:help-center) on top of the host's own surface.
                // chrome:prompt is the one
                // exception — its handlers claim conditionally (an empty text
                // must still degrade to the dock/search fallback).
                if (name !== "chrome:prompt" && e.cancelable) e.preventDefault();
                handler((e as CustomEvent).detail, e);
            };
            dispatchers.set(name, dispatcher);
            el.addEventListener(name, dispatcher);
        }
        return () => {
            for (const [name, dispatcher] of dispatchers) {
                el.removeEventListener(name, dispatcher);
            }
        };
        // Handlers are read once per mount; configs re-render rarely enough
        // that re-subscribing on identity change is not worth the churn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Suite language plane: the shared runtime (suite-theme.js) notifies every
    // document when the resolved language moves — a hub pick, the pane's own
    // pick broadcast back, or the server plane reaching a standalone window.
    // The application's own language handler is the one piece that can
    // retranslate this UI, so changes that did not originate here ride
    // through it; a pick made in THIS document already retranslated through
    // its chrome:language event, and i18n.changeLanguage is idempotent for
    // the echoed remainder.
    const languageHandlerRef = useRef(events.language);
    languageHandlerRef.current = events.language;
    const languagePropRef = useRef(language);
    languagePropRef.current = language;
    useEffect(() => {
        const runtime = (
            window as unknown as {
                SuiteTheme?: {
                    language?: string;
                    onChange?: (
                        listener: (state: { language?: string }) => void,
                    ) => () => void;
                };
            }
        ).SuiteTheme;
        if (!runtime || typeof runtime.onChange !== "function") return;
        let last: string | null = null;
        const unsubscribe = runtime.onChange((state) => {
            const code =
                state && state.language ? String(state.language) : null;
            if (!code || code === last) return;
            last = code;
            if (code !== languagePropRef.current) {
                languageHandlerRef.current?.({ code });
            }
        });
        // Mount-time reconciliation: intents raised before this adapter
        // existed — the runtime's boot application (no bar is mounted yet at
        // that moment) and a server adoption that raced the mount — never
        // reached a listener. Align this document with the suite's resolved
        // language once now; the application's handler is the one piece that
        // can retranslate the running UI. A document already in step no-ops.
        const current = runtime.language;
        if (current && current !== languagePropRef.current) {
            last = current;
            languageHandlerRef.current?.({ code: current });
        }
        return unsubscribe;
    }, []);

    // Children slot: portal the hosted toolbar into the bar's flex row
    useEffect(() => {
        // Mount even while children are momentarily falsy (a guarded strip,
        // e.g. yaz's hasDocument modes): this effect only re-runs on the
        // slotChildren toggle, so gating on children here would strand the
        // slot forever when content streams in later. The portal renders
        // nothing until then.
        if (!slotChildren) return;
        const mountSlot = (el: SuiteTopbarElement, attempt: number) => {
            if (slotRef.current) return;
            const inner = el.querySelector(".suite-chrome-inner");
            if (inner) {
                const slot = document.createElement("div");
                slot.className = "suite-chrome-app-slot";
                const anchor =
                    el.querySelector(".suite-chrome-center") ??
                    el.querySelector(".suite-chrome-right");
                inner.insertBefore(slot, anchor);
                slotRef.current = slot;
                setPortalTarget(slot);
            } else if (attempt > 0) {
                window.setTimeout(() => mountSlot(el, attempt - 1), 250);
            } else {
                // chrome.js never upgraded: host the toolbar in a bare strip
                const slot = document.createElement("div");
                slot.className = "suite-chrome-app-slot suite-chrome-app-slot--fallback";
                el.appendChild(slot);
                slotRef.current = slot;
                setPortalTarget(slot);
            }
        };
        const el = hostRef.current;
        if (el) mountSlot(el, 8);
        return () => {
            slotRef.current?.remove();
            slotRef.current = null;
            setPortalTarget(null);
        };
        // Mount once per slotChildren toggle; the portal content streams
        // through portalTarget on every render without re-mounting the slot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slotChildren]);

    // Token shim: inject once per unique shim text, shared across mounts
    useEffect(() => {
        if (!tokenShim) return;
        const style = document.createElement("style");
        style.dataset.suiteTopbarShim = String(shimSeq++);
        style.textContent = tokenShim;
        document.head.appendChild(style);
        return () => {
            style.remove();
        };
    }, [tokenShim]);

    // Height publish: layouts that reserve exact bar space read the variable
    useEffect(() => {
        if (!publishHeight) return;
        const varName =
            typeof publishHeight === "string"
                ? publishHeight
                : "--suite-topbar-height";
        const el = hostRef.current;
        if (!el) return;
        const apply = () => {
            document.documentElement.style.setProperty(
                varName,
                `${el.getBoundingClientRect().height}px`,
            );
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => {
            observer.disconnect();
            // Zero, not removed: layouts anchor with `var(--suite-topbar-h)`
            // and an unset variable would fall back to covering the bar.
            document.documentElement.style.setProperty(varName, "0px");
        };
    }, [publishHeight]);

    // chrome.js loads with `defer` ahead of this module, so the element is
    // normally defined before first render; the poll only covers the "hub
    // down" failure mode: after a short grace period the `fallback` strip
    // (if the app supplied one) replaces a bar that would never upgrade.
    const [chromeReady, setChromeReady] = useState(
        () =>
            typeof window === "undefined" ||
            Boolean(window.customElements?.get?.("suite-topbar")),
    );
    useEffect(() => {
        if (chromeReady) return;
        const started = Date.now();
        const timer = window.setInterval(() => {
            if (
                window.customElements?.get?.("suite-topbar") ||
                Date.now() - started > CHROME_GRACE_MS
            ) {
                window.clearInterval(timer);
                setChromeReady(
                    Boolean(window.customElements?.get?.("suite-topbar")),
                );
            }
        }, 250);
        return () => window.clearInterval(timer);
    }, [chromeReady]);

    const elementAvailable =
        typeof window !== "undefined" &&
        Boolean(window.customElements?.get?.("suite-topbar"));

    const bar = createElement("suite-topbar", {
        ref: hostRef,
        app,
        style: {
            ...(children || customSections ? { flex: "1 1 auto" } : {}),
            position: "relative",
            // The top of the suite chrome ladder (z-50); going above it would
            // cover hub modals and command surfaces.
            zIndex: 50,
            overflow: "visible",
            isolation: "isolate",
        },
    });

    const hostedChildren =
        slotChildren && portalTarget && children
            ? createPortal(children, portalTarget)
            : null;

    return (
        <Fragment>
            {elementAvailable || chromeReady || !fallback ? bar : fallback}
            {customSections ?? null}
            {hostedChildren ?? (slotChildren ? null : children ?? null)}
        </Fragment>
    );
}
